/**
 * Toplantı sohbeti şifrelemesi (Web Crypto API)
 *
 * v1 [TBKENC1]: Toplantı kodundan PBKDF2 ile türetilen oda anahtarı (AES-256-GCM).
 * v2 [TBKENC2]: v1 anahtarı + AAD bütünlük bağlama (mesaj kimliği, gönderen, toplantı).
 * v3 [TBKENC3]: ECDH P-256 + HKDF ile kişiye özel uçtan uca anahtar (özel mesajlar).
 *               Özel anahtar tarayıcıdan asla çıkmaz; sunucu yalnızca açık anahtarı görür.
 * v4 [TBKENC4]: Rastgele üretilen ve ECDH ile sarılarak dağıtılan E2E oda anahtarı
 *               (genel mesajlar). Sunucu bu anahtarı hiçbir biçimde türetemez.
 */

export const ENCRYPTED_MESSAGE_PREFIX = '[TBKENC1]';
export const ENCRYPTED_MESSAGE_PREFIX_V2 = '[TBKENC2]';
export const ENCRYPTED_MESSAGE_PREFIX_V3 = '[TBKENC3]';
export const ENCRYPTED_MESSAGE_PREFIX_V4 = '[TBKENC4]';
export const MEETING_CIPHER_VERSION = 4;

const PBKDF2_ITERATIONS = 120_000;
const APP_CONTEXT = 'tbkmeet-meeting-aes256-v1';
const HKDF_CONTEXT = 'tbkmeet-e2e-chat-v3';

function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('Web Crypto API kullanılamıyor (HTTPS gerekli)');
  }
  return subtle;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Toplantı kodundan AES-256 oda anahtarı türetir (PBKDF2-SHA256). */
export async function deriveMeetingAesKey(
  meetingCode: string,
  extraSecret = ''
): Promise<CryptoKey> {
  const normalized = meetingCode.trim().toLowerCase();
  if (!normalized) {
    throw new Error('Toplantı kodu gerekli');
  }

  const subtle = getSubtle();
  const password = `${APP_CONTEXT}:${normalized}:${extraSecret}`;
  const keyMaterial = await subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  const salt = new TextEncoder().encode(`${APP_CONTEXT}:${normalized}`);

  return subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// ---------------------------------------------------------------------------
// ECDH P-256: uçtan uca kişiye özel anahtarlar (özel mesajlar için)
// ---------------------------------------------------------------------------

export interface EcdhKeyPair {
  privateKey: CryptoKey;
  /** Base64 raw formatta açık anahtar; sunucu üzerinden paylaşılır. */
  publicKeyB64: string;
}

/** Katılım başına geçici ECDH anahtar çifti üretir. Özel anahtar dışa aktarılamaz. */
export async function generateEcdhKeyPair(): Promise<EcdhKeyPair> {
  const subtle = getSubtle();
  const pair = await subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits']
  );
  const raw = await subtle.exportKey('raw', pair.publicKey);
  return {
    privateKey: pair.privateKey,
    publicKeyB64: toBase64(new Uint8Array(raw)),
  };
}

/**
 * Karşı tarafın açık anahtarıyla ortak AES-256-GCM anahtarı türetir
 * (ECDH → HKDF-SHA256). İki taraf da aynı anahtara ulaşır.
 */
export async function derivePairwiseAesKey(
  myPrivateKey: CryptoKey,
  remotePublicKeyB64: string,
  meetingCode: string
): Promise<CryptoKey> {
  const subtle = getSubtle();
  const remotePublicKey = await subtle.importKey(
    'raw',
    fromBase64(remotePublicKeyB64),
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    []
  );

  const sharedBits = await subtle.deriveBits(
    { name: 'ECDH', public: remotePublicKey },
    myPrivateKey,
    256
  );

  const hkdfKey = await subtle.importKey('raw', sharedBits, 'HKDF', false, ['deriveKey']);

  return subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode(`${HKDF_CONTEXT}:${meetingCode.trim().toLowerCase()}`),
      info: new TextEncoder().encode(HKDF_CONTEXT),
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// ---------------------------------------------------------------------------
// E2E oda anahtarı: rastgele üretilir, ECDH ikili anahtarıyla sarılarak dağıtılır
// ---------------------------------------------------------------------------

export interface E2eRoomKey {
  key: CryptoKey;
  /** Sarıp dağıtabilmek için ham anahtar (base64, yalnızca bellekte tutulur) */
  rawB64: string;
  /** Epoch çözümleme: en eski (createdAt, creatorId) anahtar kazanır */
  createdAt: number;
  creatorId: string;
}

async function importRoomKeyRaw(rawB64: string): Promise<CryptoKey> {
  return getSubtle().importKey('raw', fromBase64(rawB64), { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

/** Kriptografik olarak rastgele 256-bit E2E oda anahtarı üretir. */
export async function generateE2eRoomKey(creatorId: string): Promise<E2eRoomKey> {
  const rawBytes = crypto.getRandomValues(new Uint8Array(32));
  const rawB64 = toBase64(rawBytes);
  return {
    key: await importRoomKeyRaw(rawB64),
    rawB64,
    createdAt: Date.now(),
    creatorId,
  };
}

/** Ham oda anahtarını ECDH ikili anahtarıyla sarar (yalnızca alıcı açabilir). */
export async function wrapE2eRoomKey(
  roomKey: E2eRoomKey,
  pairwiseKey: CryptoKey
): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(roomKey.rawB64);
  const ciphertext = await getSubtle().encrypt({ name: 'AES-GCM', iv }, pairwiseKey, encoded);
  return `${toBase64(iv)}.${toBase64(new Uint8Array(ciphertext))}`;
}

/** Sarılmış oda anahtarını açar ve kullanılabilir E2eRoomKey döndürür. */
export async function unwrapE2eRoomKey(
  wrapped: string,
  pairwiseKey: CryptoKey,
  createdAt: number,
  creatorId: string
): Promise<E2eRoomKey> {
  const dot = wrapped.indexOf('.');
  if (dot <= 0) {
    throw new Error('Geçersiz sarılmış anahtar formatı');
  }
  const iv = fromBase64(wrapped.slice(0, dot));
  const ciphertext = fromBase64(wrapped.slice(dot + 1));
  const decrypted = await getSubtle().decrypt({ name: 'AES-GCM', iv }, pairwiseKey, ciphertext);
  const rawB64 = new TextDecoder().decode(decrypted);
  return {
    key: await importRoomKeyRaw(rawB64),
    rawB64,
    createdAt,
    creatorId,
  };
}

/** İki anahtar adayından hangisinin geçerli olacağını belirler (eski olan kazanır). */
export function isPreferredRoomKey(candidate: E2eRoomKey, current: E2eRoomKey | null): boolean {
  if (!current) return true;
  if (candidate.createdAt !== current.createdAt) {
    return candidate.createdAt < current.createdAt;
  }
  return candidate.creatorId < current.creatorId;
}

// ---------------------------------------------------------------------------
// Şifreleme / çözme
// ---------------------------------------------------------------------------

/**
 * AAD (ek doğrulanmış veri): şifreyi mesaj bağlamına bağlar.
 * Saldırgan ciphertext'i başka mesaj kimliği/gönderen altında tekrar oynatamaz.
 */
export interface ChatCipherContext {
  meetingId: string;
  messageId: string;
  senderId: string;
}

function buildAad(ctx: ChatCipherContext): Uint8Array {
  return new TextEncoder().encode(
    `${ctx.meetingId.trim().toLowerCase()}|${ctx.messageId}|${ctx.senderId}`
  );
}

export function isEncryptedChatText(value: string): boolean {
  return (
    value.startsWith(ENCRYPTED_MESSAGE_PREFIX) ||
    value.startsWith(ENCRYPTED_MESSAGE_PREFIX_V2) ||
    value.startsWith(ENCRYPTED_MESSAGE_PREFIX_V3)
  );
}

async function encryptWithPrefix(
  plaintext: string,
  key: CryptoKey,
  prefix: string,
  ctx?: ChatCipherContext
): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const params: AesGcmParams = { name: 'AES-GCM', iv };
  if (ctx) {
    params.additionalData = buildAad(ctx);
  }
  const ciphertext = await getSubtle().encrypt(params, key, encoded);
  return `${prefix}${toBase64(iv)}.${toBase64(new Uint8Array(ciphertext))}`;
}

async function decryptBody(
  body: string,
  key: CryptoKey,
  ctx?: ChatCipherContext
): Promise<string> {
  const dot = body.indexOf('.');
  if (dot <= 0) {
    throw new Error('Geçersiz şifreli mesaj formatı');
  }
  const iv = fromBase64(body.slice(0, dot));
  const ciphertext = fromBase64(body.slice(dot + 1));
  const params: AesGcmParams = { name: 'AES-GCM', iv };
  if (ctx) {
    params.additionalData = buildAad(ctx);
  }
  const decrypted = await getSubtle().decrypt(params, key, ciphertext);
  return new TextDecoder().decode(decrypted);
}

/** Oda anahtarıyla şifreler. ctx verilirse v2 (AAD'li), verilmezse v1 üretir. */
export async function encryptChatText(
  plaintext: string,
  key: CryptoKey,
  ctx?: ChatCipherContext
): Promise<string> {
  if (ctx) {
    return encryptWithPrefix(plaintext, key, ENCRYPTED_MESSAGE_PREFIX_V2, ctx);
  }
  return encryptWithPrefix(plaintext, key, ENCRYPTED_MESSAGE_PREFIX);
}

/** Kişiye özel (ECDH) anahtarla şifreler — yalnızca alıcı çözebilir. */
export async function encryptPrivateChatText(
  plaintext: string,
  pairwiseKey: CryptoKey,
  ctx: ChatCipherContext
): Promise<string> {
  return encryptWithPrefix(plaintext, pairwiseKey, ENCRYPTED_MESSAGE_PREFIX_V3, ctx);
}

/** E2E oda anahtarıyla şifreler (genel mesajlar) — sunucu çözemez. */
export async function encryptRoomE2eText(
  plaintext: string,
  roomKey: CryptoKey,
  ctx: ChatCipherContext
): Promise<string> {
  return encryptWithPrefix(plaintext, roomKey, ENCRYPTED_MESSAGE_PREFIX_V4, ctx);
}

export function getChatCipherVersion(payload: string): 0 | 1 | 2 | 3 | 4 {
  if (payload.startsWith(ENCRYPTED_MESSAGE_PREFIX_V4)) return 4;
  if (payload.startsWith(ENCRYPTED_MESSAGE_PREFIX_V3)) return 3;
  if (payload.startsWith(ENCRYPTED_MESSAGE_PREFIX_V2)) return 2;
  if (payload.startsWith(ENCRYPTED_MESSAGE_PREFIX)) return 1;
  return 0;
}

/**
 * Şifreli sohbet metnini çözer. Sürüme göre doğru anahtar verilmelidir:
 * v1/v2 → türetilmiş oda anahtarı, v3 → kişiye özel anahtar, v4 → E2E oda anahtarı.
 */
export async function decryptChatText(
  payload: string,
  key: CryptoKey,
  ctx?: ChatCipherContext
): Promise<string> {
  const version = getChatCipherVersion(payload);
  if (version === 0) {
    return payload;
  }

  if (version === 1) {
    return decryptBody(payload.slice(ENCRYPTED_MESSAGE_PREFIX.length), key);
  }

  if (!ctx) {
    throw new Error('AAD bağlamı olmadan v2/v3/v4 mesajı çözülemez');
  }

  const prefix =
    version === 4
      ? ENCRYPTED_MESSAGE_PREFIX_V4
      : version === 3
        ? ENCRYPTED_MESSAGE_PREFIX_V3
        : ENCRYPTED_MESSAGE_PREFIX_V2;
  return decryptBody(payload.slice(prefix.length), key, ctx);
}
